import type {
  ISubscription,
  PayCardGroupButtonClickParams,
  SupportedLanguages,
} from '@wegic/price-ui';
import {
  EBillInterval,
  PayCardGroup,
  PlanSection,
  PricingModalOrigin,
  useSubscribeStore,
} from '@wegic/price-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AVATAR_URL_PREFIX } from '@/const';
import { filterPricingProductsForSubscription } from '@/helpers/pricingProductsForUser';
import { setPricingModalOrigin, setSelectProduct } from '@/services/pay/storage';
import { getProductsRequest, getUserSubscriptionRequest } from '@/services/user';
import { useI18nStore } from '@/stores/i18n';
import { usePricingModalStore } from '@/stores/pricingModal';
import { useRemixUpgradeModalStore } from '@/stores/remixUpgradeModal';
import { useUserStore } from '@/stores/user';
import styles from './index.module.less';

export default function SubscriptionTab() {
  const productList = usePricingModalStore(s => s.productList);
  const setProductList = usePricingModalStore(s => s.setProductList);
  const builderUserInfo = useUserStore(s => s.userInfo);
  const builderUserSubscription = useUserStore(s => s.userSubscription);
  const builderUserCoupons = useUserStore(s => s.userCoupons);
  const currentLanguage = useI18nStore(s => s.currentLanguage);

  const [userSubscription, setUserSubscription] = useState<ISubscription | null>(
    builderUserSubscription,
  );

  useEffect(() => {
    if (productList.length > 0 && userSubscription) {
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        const [productsRes, subscriptionRes] = await Promise.all([
          productList.length === 0 ? getProductsRequest() : null,
          !userSubscription ? getUserSubscriptionRequest() : null,
        ]);

        if (cancelled) {
          return;
        }

        if (subscriptionRes?.code === 0) {
          setUserSubscription(subscriptionRes.data);
          useUserStore.getState().setUserSubscription(subscriptionRes.data);
        }
        if (productsRes?.code === 0) {
          const nextSubscription = subscriptionRes?.code === 0
            ? subscriptionRes.data
            : userSubscription;
          setProductList(await filterPricingProductsForSubscription(productsRes.data.products, nextSubscription));
        }
      } catch { /* ignore */ }
    };

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, [productList.length, setProductList, userSubscription]);

  const priceUserInfo = useMemo(() => {
    if (!builderUserInfo) {
      return undefined;
    }

    return {
      id: builderUserInfo.userId,
      name: builderUserInfo.userName,
      avatar: builderUserInfo.userImage ? `${AVATAR_URL_PREFIX}/${builderUserInfo.userImage}` : '',
      email: builderUserInfo.email,
    };
  }, [builderUserInfo]);

  const handleCardButtonClick = useCallback((params: PayCardGroupButtonClickParams) => {
    setSelectProduct(params.product);
    useSubscribeStore.getState().setSelectUpgradeProductId(params.product.id);
    setPricingModalOrigin(PricingModalOrigin.RemixPlanModelUpgrade);
    useRemixUpgradeModalStore.getState().close();
    usePricingModalStore.getState().open(PlanSection.PLAN_LANDING);
  }, []);

  return (
    <div className={styles['subscription-tab']}>
      <PayCardGroup
        className={styles['subscription-tab__group']}
        cardClassName={styles['subscription-pay-card']}
        applicationType="builder"
        defaultBillInterval={EBillInterval.Year}
        currentLanguage={currentLanguage as SupportedLanguages}
        products={productList}
        userSubscription={userSubscription ?? builderUserSubscription}
        userInfo={priceUserInfo}
        userCoupons={builderUserCoupons ?? []}
        isRenew={false}
        showTrialPack={false}
        onCardButtonClick={handleCardButtonClick}
      />
    </div>
  );
}
